import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/axios";
import useAuthStore from "../../store/auth.store";
import useCaseImg from "../../assets/use_case.png";

// ─── Defaults (used as fallback if /api/landing/ is empty/unreachable) ───────
const DEFAULT_LANDING = {
  hero: {
    kicker: "The future of cricket data",
    title_line1: "Ball-by-Ball Data",
    title_line2: "Collection,",
    title_emphasis: "Simplified.",
    subtitle:
      "Record every delivery, analyze every shot, and unlock insights that transform how coaches, analysts, and teams understand the game. No expertise required.",
    cta_primary_text: "Start free trial",
    cta_primary_url: "/signup",
    cta_secondary_text: "See how it works",
    cta_secondary_url: "#features",
    badges: ["No credit card required", "30-day free trial", "Built for cricket professionals"],
  },
  features: [
    { title: "Ball-by-Ball Scoring", desc: "Record every delivery with detailed annotations — runs, extras, shot type, fielding position, dismissal — in real time. Works on any device, online or offline." },
    { title: "Team & Player Management", desc: "Manage teams, squads, and player profiles in one place. Track individual performance across matches, tournaments and seasons." },
    // 2026-06-05 — "Advanced Analytics" renamed to "AI Analytics" + a
    // richer description that ties the card to the actual AI features
    // operators see in the app (AI Score, AI Tag, AI Press Report).
    { title: "AI Analytics", desc: "Let Claude assist the scoring. AI Score reads the video and pre-fills runs / extras / wicket. AI Tag pre-fills shot type, foot movement, line/length. Plus AI Press Reports and a per-ball chatbot trained on your match." },
    { title: "Scorecard + Press Report PDFs", desc: "One-click scorecard PDFs and Claude-written press reports — formatted like a sports journalist would. Downloadable, brandable, shareable." },
    { title: "Mobile + Tablet Ready", desc: "Score from a phone in the stands or a tablet in the dugout. Same data, same scorecard, same wallet — the SPA syncs automatically." },
    { title: "API & Data Export", desc: "Pull your match data into your own dashboards via our REST API, or export CSV for spreadsheets. Your data, your formats." },
  ],
  steps: [
    { num: "1", title: "Create account", desc: "Sign up in seconds. No credit card required. Pick a plan and start your 30-day free trial immediately.", tag: "Takes 30 seconds" },
    { num: "2", title: "Score matches", desc: "Use our intuitive interface to record ball-by-ball data during matches. Works on tablets, phones, and laptops.", tag: "Real-time tracking" },
    { num: "3", title: "Generate insights", desc: "Instantly access detailed analytics, player statistics, and exportable reports for selectors, coaches, and broadcasters.", tag: "Exportable reports" },
  ],
  use_cases: [
    // 2026-06-05 — expanded from 4 to 8 use cases per founder ask.
    // Each kept short (one-line value prop) so the card grid stays
    // skimmable. Order: B2C scorer / analyst / academy first, then
    // B2B associations + leagues + broadcasters + agents + clubs.
    { title: "Freelance Scorers", desc: "Charge ₹1.5-2k a day for local-tournament scoring. The platform pays for itself in 1.5 days of work — bring your own laptop, run a whole league from it." },
    { title: "Team Analysts", desc: "Prepare opposition dossiers, dismissal heatmaps and bowler-by-bowler matchups in minutes. AI Tag fills the analytics fields so you're not coding 240 balls by hand." },
    { title: "Cricket Academies", desc: "Track every student across every match. Show parents proof of development with shareable scorecards, performance graphs and AI-generated press reports." },
    { title: "State + District Associations", desc: "Run an entire season on one canonical scoring system. Federation contracts include AI Tag, custom reports and unlimited scorers under one organization account." },
    { title: "Broadcasters & Press", desc: "Live ball-by-ball feed for commentary teams + Claude-written match reports for the next day's print. WhatsApp + email delivery built in." },
    { title: "Cricket Coaches", desc: "Identify which deliveries your batter struggles against and which shot is leaking wickets — straight from the AI-tagged wagon wheel and pitch map." },
    { title: "Player Agents + Talent Scouts", desc: "Find a player at find-a-player. Filter by format, role, region, recent form — backed by real ball-by-ball data, not just gut feel." },
    { title: "Clubs + Local Leagues", desc: "One organization account for all your teams, a shared player pool, a shared scorers roster. Everyone scores the same way; everyone sees the same numbers." },
  ],
  // Fallback static plans — only renders if /api/landing/ fails.
  // Source of truth is apps/landing/models.py _default_plans() + the live
  // singleton row at /admin/landing/landingpage/. Keep these in sync if
  // pricing changes again.
  plans: [
    {
      name: "Free trial", tagline: "30 days to test everything — including auto-tag and AI suggest", price: "₹0", period: "/30 days",
      features: ["100 credits to explore the platform", "All features unlocked during trial", "Ball-by-ball scoring", "Reports + CSV export", "No card required"],
      cta_text: "Start free trial", cta_url: "/signup", popular: false,
    },
    {
      name: "Basic", tagline: "For solo scorers covering a club or local team", price: "₹2,999", period: "/month",
      features: ["300 credits / month", "2 GB storage", "Ball-by-ball scoring + edit", "Reports + CSV export", "Offline mode", "Email support"],
      cta_text: "Start free trial", cta_url: "/signup", popular: false,
    },
    {
      name: "Intermediate", tagline: "For analysts and academies covering multiple teams", price: "₹4,999", period: "/month",
      features: ["700 credits / month", "5 GB storage", "Everything in Basic", "Voice scoring + Sonnet chat", "Priority email support"],
      cta_text: "Start free trial", cta_url: "/signup", popular: true,
    },
    {
      name: "Advanced", tagline: "For power users running a full season of competitive cricket", price: "₹8,999", period: "/month",
      features: ["1,500 credits / month", "10 GB storage", "Everything in Intermediate", "Match report PDF + AI ingest", "Priority email support"],
      cta_text: "Start free trial", cta_url: "/signup", popular: false,
    },
    {
      name: "Organization", tagline: "For broadcasters, leagues, federations and franchises", price: "Contact sales", period: "",
      features: ["All features unlocked, no per-feature charges", "AI auto-tag + AI Suggest included", "10 GB storage / seat (add-on ₹20/GB)", "Multi-user RBAC + API access", "Dedicated account manager"],
      cta_text: "Contact sales", cta_url: "mailto:a.manivannan@sportingzenminds.com?subject=ZenCricketData%20Organization%20enquiry", popular: false,
    },
  ],
  cta: {
    title: "Ready to transform your",
    title_emphasis: "cricket data workflow?",
    subtitle: "Join the cricket teams using ZenCricketData. Start your free trial today — no credit card required.",
    badges: ["Secure & private", "30-day free trial", "No credit card", "Cancel anytime"],
  },
};

// ─── Reusable icons ──────────────────────────────────────────────────────────
const CheckIcon = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);
const CheckCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);
const ArrowRightIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

// Icon set for features (cycled by index)
const featureIcons = [
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />,
];
const useCaseIcons = [
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />,
];

// ─── Inline navbar / footer ──────────────────────────────────────────────────
function LandingNavbar() {
  // 2026-06-10 — the landing now renders for authenticated users too
  // (RootGate stopped auto-redirecting to dashboard). Show "Go to your
  // dashboard" instead of Sign in / Sign up when logged in.
  //
  // 2026-06-11 (founder follow-up) — tightened the gate: a not-signed-
  // in visitor was occasionally seeing the dashboard CTA. Require the
  // auth probe to have settled (`!loading`) AND a populated user
  // record before treating the visitor as signed in. Any not-fully-
  // authenticated state (loading, transient flag-only, post-logout
  // with stale cookie) falls through to Sign in / Sign up.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const dashHref = user?.is_superuser ? "/admin" : "/dashboard";
  const showDashboardCTA = !loading && isAuthenticated && Boolean(user?.id);
  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-black/60 backdrop-blur border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* 2026-06-05 (v2) — logo image removed per founder follow-up.
            Wordmark-only header again. */}
        <Link to="/" className="font-extrabold text-xl tracking-tight text-white">
          Zen<span className="text-[#e53935]">cricket</span>data
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-gray-300">
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
          <a href="#use-cases" className="hover:text-white transition-colors">Use cases</a>
          {/* 2026-06-05 — new in-page section explaining Zencredits */}
          <a href="#zencredits" className="hover:text-white transition-colors">Zencredits</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          {/* T247.J — public marketplace entry */}
          <Link to="/find-a-scorer" className="hover:text-white transition-colors">Find a scorer</Link>
          {/* T264 — player marketplace entry */}
          <Link to="/find-a-player" className="hover:text-white transition-colors">Find a player</Link>
        </nav>
        <div className="flex items-center gap-2">
          {showDashboardCTA ? (
            <Link
              to={dashHref}
              className="text-sm bg-[#e53935] hover:bg-red-700 text-white font-medium px-4 py-1.5 rounded-md transition-colors"
            >
              Go to your dashboard →
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-gray-300 hover:text-white px-3 py-1.5 transition-colors">Sign in</Link>
              <Link to="/signup" className="text-sm bg-[#e53935] hover:bg-red-700 text-white font-medium px-4 py-1.5 rounded-md transition-colors">Sign up</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function LandingFooter() {
  return (
    <footer className="bg-black border-t border-white/10 px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-7xl mx-auto text-center text-gray-400 text-sm">
        <div className="font-extrabold text-xl text-white mb-3">
          Zen<span className="text-[#e53935]">cricket</span>data
        </div>
        <p className="mb-2">Zenminds Sporting Technologies Pvt Ltd · Chennai, India</p>
        <p>&copy; {new Date().getFullYear()} ZenCricketData. All rights reserved.</p>
        <div className="mt-4 flex items-center justify-center gap-6 text-xs">
          <Link to="/signup" className="hover:text-white transition-colors">Sign up</Link>
          <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
          <Link to="/find-a-scorer" className="hover:text-white transition-colors">Find a scorer</Link>
          <Link to="/find-a-player" className="hover:text-white transition-colors">Find a player</Link>
          <a href="mailto:a.manivannan@sportingzenminds.com" className="hover:text-white transition-colors">Contact</a>
        </div>
      </div>
    </footer>
  );
}

// ─── HomePage ────────────────────────────────────────────────────────────────
export default function Home() {
  const [data, setData] = useState(DEFAULT_LANDING);
  const [activePlan, setActivePlan] = useState(null);

  // Fetch the editable landing-page content from the API. Falls back to the
  // hardcoded DEFAULT_LANDING above if the API is unreachable or empty so the
  // page never renders blank.
  useEffect(() => {
    api.get("/api/landing/")
      .then((r) => {
        if (r?.data && typeof r.data === "object" && Object.keys(r.data).length > 0) {
          setData((prev) => ({ ...prev, ...r.data }));
        }
      })
      .catch((err) => {
        // Don't surface to the user (we have a sensible default), but log so
        // outages of /api/landing/ are visible in browser devtools.
        // eslint-disable-next-line no-console
        console.warn("landing data fetch failed:", err?.message || err);
      });
  }, []);

  // Pre-select the "popular" plan once plans load; depend ONLY on data.plans.
  // (Adding activePlan would cause this effect to re-run every time it sets
  // activePlan, which is wasteful even though the early-return prevents a loop.)
  useEffect(() => {
    if (data.plans?.length) {
      const popular = data.plans.find((p) => p.popular);
      setActivePlan(popular ? popular.name : data.plans[0].name);
    }
  }, [data.plans]);

  const heroBg = "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80";

  return (
    <div className="font-sans antialiased text-gray-800 bg-white">
      <LandingNavbar />

      {/* HERO — heights tuned per breakpoint so the stadium background
          doesn't swallow the entire viewport. 2026-05-27 founder report
          (PC pass): on a 1366×768 / 1280×800 laptop the previous
          min-h-[750px] filled the visible area completely so the CTAs,
          Features cards and nav anchors below the fold were hidden.
          New ladder: 460 (phone) → 560 sm → 600 md → 640 lg → 680 xl.
          On 1080p+ desktop this leaves ~400px of the Features section
          visible below the fold; on 768p laptops ~120px. */}
      <div className="relative pt-16 pb-12 sm:pb-0 min-h-[460px] sm:min-h-[560px] md:min-h-[600px] lg:min-h-[640px] xl:min-h-[680px] flex items-center bg-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={heroBg} alt="Stadium background" className="w-full h-full object-cover object-center opacity-60 sm:opacity-65 lg:opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 sm:via-black/80 to-black/40 sm:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#e53935]/30 via-red-900/50 to-transparent mix-blend-multiply" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl text-white pt-6 sm:pt-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-effect-light mb-6 text-xs text-gray-200 font-medium tracking-wide">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#e53935] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#e53935]" />
              </span>
              {data.hero.kicker}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight mb-6">
              {data.hero.title_line1}<br />
              {data.hero.title_line2}<br />
              <span className="text-[#e53935] drop-shadow-lg">{data.hero.title_emphasis}</span>
            </h1>
            <p className="text-lg text-gray-300 mb-10 max-w-xl leading-relaxed">{data.hero.subtitle}</p>
            <div className="flex flex-wrap items-center gap-4 mb-12">
              <Link to={data.hero.cta_primary_url || "/signup"} className="bg-[#e53935] hover:bg-red-700 text-white px-8 py-3.5 rounded-lg text-base font-semibold transition-all duration-300 shadow-xl shadow-red-500/20 flex items-center gap-2 group">
                {data.hero.cta_primary_text}
                <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={data.hero.cta_secondary_url || "#features"} className="glass-effect-light hover:bg-white/10 text-white px-8 py-3.5 rounded-lg text-base font-semibold transition-all duration-300 flex items-center gap-2">
                {data.hero.cta_secondary_text}
                <ArrowRightIcon className="w-4 h-4 transform" />
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-400">
              {data.hero.badges.map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-[#e53935]" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* FEATURES — section py-24 → py-16 lg:py-20 (was eating ~192px
          of vertical space per section). Background image opacity
          dropped from 40% to 15% so feature cards stand out cleanly
          against a near-black backdrop instead of competing with a
          stadium photo. */}
      <section id="features" className="relative py-16 lg:py-20 bg-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={heroBg} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/90 to-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-effect-light mb-6 text-xs text-[#e53935] font-bold tracking-widest uppercase border border-red-500/30">Powerful features</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Everything you need for <br className="hidden sm:block" />
              <span className="text-[#e53935] drop-shadow-lg">cricket data work</span>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto">From live match recording to comprehensive reports — ZenCricketData gives your team the tools to turn raw cricket data into actionable insight.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.features.map((f, i) => (
              <div key={f.title} className="glass-effect border border-white/10 p-8 rounded-2xl hover:shadow-xl hover:shadow-red-900/20 hover:border-white/20 hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-12 h-12 bg-[#e53935] rounded-xl flex items-center justify-center text-white mb-6 shadow-lg shadow-red-500/30 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">{featureIcons[i % featureIcons.length]}</svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#e53935] transition-colors">{f.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — same treatment: tighter py, dimmer bg image so
          the 3-step rail is the visual focus. */}
      <section id="how-it-works" className="relative py-16 lg:py-20 bg-black overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1593341646782-e0b495cff86d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover opacity-10 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/95 to-black/80" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full glass-effect-light mb-6 text-xs text-[#e53935] font-bold tracking-widest uppercase border border-red-500/30">Simple process</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Get started in <span className="text-[#e53935] drop-shadow-lg">3 easy steps</span>
            </h2>
          </div>
          <div className="relative max-w-5xl mx-auto">
            <div className="hidden md:block absolute top-[40px] left-[15%] right-[15%] h-[2px] bg-gradient-to-r from-transparent via-[#e53935]/50 to-transparent z-0" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12 relative z-10">
              {data.steps.map((step) => (
                <div key={step.num} className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-[#e53935] rounded-full flex items-center justify-center text-white text-3xl font-bold mb-6 shadow-[0_0_30px_rgba(229,57,53,0.4)] border-4 border-black z-10">{step.num}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4 max-w-xs">{step.desc}</p>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-[#e53935]">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                    {step.tag}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section id="use-cases" className="relative py-16 lg:py-20 bg-black overflow-hidden border-t border-white/5">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full glass-effect-light border border-red-500/30 text-[#e53935] text-xs font-bold tracking-widest uppercase mb-6">Use cases</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">
                Built for every <span className="text-[#e53935] drop-shadow-lg">cricket<br />professional</span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-xl">Whether you run a state association, coach a club, score for a league, or anchor a broadcast — ZenCricketData adapts to how you work.</p>
              <div className="space-y-4 mb-10">
                {data.use_cases.map((uc, i) => (
                  <div key={uc.title} className="glass-effect border border-white/10 p-5 rounded-2xl flex items-start gap-5 hover:bg-white/5 hover:border-white/20 transition-all">
                    <div className="w-12 h-12 bg-[#e53935] rounded-[14px] flex-shrink-0 flex items-center justify-center text-white shadow-md shadow-red-500/20">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">{useCaseIcons[i % useCaseIcons.length]}</svg>
                    </div>
                    <div>
                      <h3 className="text-white font-bold mb-1 text-[15px]">{uc.title}</h3>
                      <p className="text-gray-400 text-[13px] leading-relaxed pr-4">{uc.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/signup" className="inline-flex items-center gap-2 bg-[#e53935] hover:bg-red-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 shadow-lg shadow-red-500/20">
                Get started today
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative mt-8 lg:mt-0 pl-0 lg:pl-10">
              {/* Use-case illustration. aspect-video on phone so it
                  doesn't dominate the section; tighter ratios as the
                  viewport grows. */}
              <div className="bg-gray-900 rounded-[2rem] overflow-hidden shadow-2xl shadow-red-900/20 aspect-video sm:aspect-[4/3] lg:aspect-[4/3] w-full max-w-[500px] mx-auto lg:ml-auto relative border border-white/5">
                <img src={useCaseImg} alt="Cricket ball and stumps" className="w-full h-full object-cover opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ZENCREDITS EXPLAINER — added 2026-06-05 per founder ask.
          Plain-language walkthrough of what Zencredits are, what each
          AI feature costs, where they come from, and how they're spent.
          Sits between Use Cases and Pricing so the reader has context
          before the plan grid talks about credit allocations. */}
      <section id="zencredits" className="relative py-16 lg:py-20 bg-gradient-to-b from-black to-zinc-950 overflow-hidden border-t border-white/5">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center px-3 py-1 rounded-full glass-effect-light border border-amber-500/40 text-amber-300 text-xs font-bold tracking-widest uppercase mb-6">Zencredits · How AI is billed</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">
              The fairest way to pay <br />for cricket AI.
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              <span className="font-bold text-white">1 Zencredit = ₹1.</span> Your plan grants a monthly allowance.
              You spend a few credits each time the app runs a paid AI call. Cricket scoring itself is always free — credits only cover the AI surfaces below.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {/* Card 1 — Where credits come from */}
            <div className="glass-effect border border-amber-500/20 rounded-2xl p-6">
              <div className="w-11 h-11 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/></svg>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Where credits come from</h3>
              <ul className="text-gray-400 text-sm space-y-1.5 leading-relaxed">
                <li>· <span className="text-white">Plan allowance</span> — refilled every month</li>
                <li>· <span className="text-white">Topups</span> — ₹99 / ₹299 / ₹999 / ₹2,999 packs with bonus credits up to +25%</li>
                <li>· <span className="text-white">Trial</span> — 100 credits free to play with everything for 30 days</li>
              </ul>
            </div>

            {/* Card 2 — What each AI feature costs */}
            <div className="glass-effect border border-amber-500/20 rounded-2xl p-6">
              <div className="w-11 h-11 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">What each AI call costs</h3>
              <ul className="text-gray-400 text-sm space-y-1.5 leading-relaxed font-mono">
                <li><span className="text-white">AI Score</span> — 2 ZC <span className="text-gray-500">(per ball)</span></li>
                <li><span className="text-white">AI Tag</span> — 7 ZC <span className="text-gray-500">(per ball)</span></li>
                <li><span className="text-white">Commentary regen</span> — 3 ZC <span className="text-gray-500">(per ball)</span></li>
                <li><span className="text-white">Scorecard PDF</span> — 5 ZC <span className="text-gray-500">(per match)</span></li>
                <li><span className="text-white">AI Press Report</span> — 20 ZC <span className="text-gray-500">(per match)</span></li>
                <li><span className="text-white">AI Match Summary</span> — <span className="text-emerald-300">Free</span></li>
              </ul>
            </div>

            {/* Card 3 — How to keep an eye on it */}
            <div className="glass-effect border border-amber-500/20 rounded-2xl p-6">
              <div className="w-11 h-11 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">No surprises</h3>
              <ul className="text-gray-400 text-sm space-y-1.5 leading-relaxed">
                <li>· Every paid button shows the cost <span className="text-white">before</span> you click</li>
                <li>· Your <span className="text-white">wallet balance</span> sits in the app's top bar at all times</li>
                <li>· You see a <span className="text-white">"Confirm N ZC"</span> modal before any non-trivial spend</li>
                <li>· If a call fails, we <span className="text-white">refund the credits automatically</span></li>
              </ul>
            </div>
          </div>

          <div className="text-center text-gray-500 text-sm max-w-2xl mx-auto">
            B2B contracts and Federation plans bundle every AI feature into the subscription — no per-call ZC accounting. The breakdown above applies to B2C and pay-as-you-go plans.
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative py-16 lg:py-20 bg-black overflow-hidden border-t border-white/5">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="inline-flex px-4 py-1.5 rounded-full glass-effect-light mb-6 text-xs text-[#e53935] font-bold tracking-widest uppercase border border-red-500/30">Simple pricing</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Plans for <span className="text-[#e53935] drop-shadow-lg">every team</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">No hidden fees, no surprises. Cancel anytime. Start with a 30-day free trial — no card required.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-center">
            {data.plans.map((plan) => {
              const isActive = activePlan === plan.name;
              const isExternal = plan.cta_url?.startsWith("mailto:") || plan.cta_url?.startsWith("http");
              const ctaCls = `w-full py-4 rounded-xl font-bold text-center transition-all duration-300 ${isActive ? "bg-white text-[#e53935] hover:bg-gray-100" : "bg-[#e53935] text-white hover:bg-red-700 shadow-lg shadow-red-500/20"}`;
              return (
                <div
                  key={plan.name}
                  onClick={() => setActivePlan(plan.name)}
                  className={`relative cursor-pointer flex flex-col h-full transition-all duration-300 rounded-[2rem] p-8 md:p-10 ${
                    isActive ? "bg-[#e53935] border border-red-500 shadow-[0_0_40px_rgba(229,57,53,0.3)] scale-[1.03] z-10" : "glass-effect border border-white/10 hover:border-white/20 z-0"
                  }`}
                >
                  {plan.popular && (
                    <div className={`absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-[#e53935] text-[11px] font-black uppercase tracking-widest py-1.5 px-4 rounded-full shadow-lg ${isActive ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}>
                      Most popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-white mb-2 mt-2">{plan.name}</h3>
                  <p className={`text-sm mb-6 transition-colors ${isActive ? "text-red-100" : "text-gray-400"}`}>{plan.tagline}</p>
                  <div className="mb-8">
                    <span className="text-5xl font-extrabold text-white">{plan.price}</span>
                    {plan.period && <span className={`font-medium transition-colors ${isActive ? "text-red-200" : "text-gray-400"}`}>{plan.period}</span>}
                  </div>
                  <ul className="space-y-4 mb-8 flex-1">
                    {plan.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-3">
                        <div className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? "bg-white/20 text-white" : "bg-[#e53935]/20 text-[#e53935]"}`}>
                          <CheckIcon />
                        </div>
                        <span className={`transition-colors ${isActive ? "text-white" : "text-gray-300"}`}>{feat}</span>
                      </li>
                    ))}
                  </ul>
                  {isExternal ? (
                    <a href={plan.cta_url} onClick={(e) => e.stopPropagation()} className={ctaCls}>{plan.cta_text}</a>
                  ) : (
                    <Link to={plan.cta_url || "/signup"} onClick={(e) => e.stopPropagation()} className={ctaCls}>{plan.cta_text}</Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-16 lg:py-24 bg-black overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1531415074968-036ba1b575da?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-[#0a0a0a]/80" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-8 pb-12">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">
            {data.cta.title} <span className="text-[#e53935] drop-shadow-md">{data.cta.title_emphasis}</span>
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto mb-10">{data.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link to="/signup" className="w-full sm:w-auto bg-[#e53935] hover:bg-red-700 text-white px-8 py-3.5 rounded-lg text-base font-semibold transition-all duration-300 shadow-xl shadow-red-500/20 flex items-center justify-center gap-2">
              Start your free trial
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
            <a href="#pricing" className="w-full sm:w-auto glass-effect-light hover:bg-white/10 text-white px-8 py-3.5 rounded-lg text-base font-semibold transition-all duration-300 flex items-center justify-center gap-2">See pricing</a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-sm text-gray-400 font-medium">
            {data.cta.badges.map((badge) => (
              <div key={badge} className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-[#e53935]/80" />
                {badge}
              </div>
            ))}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
